import classNames from "classnames";
import { useCallback, useEffect, useState } from "react";
import AscButton from "../../AscButton/AscButton";
import AscFindTheWordsPuzzleCell from "../AscFindTheWordsPuzzleCell/AscFindTheWordsPuzzleCell";
import styles from "./AscFindTheWordsPuzzleBoard.module.scss";
import {
  AscFindTheWordsPuzzleBoardSize,
  AscFindTheWordsPuzzleBoardProps,
  AscFindTheWordsPuzzleCellType,
  AscFindTheWordsPuzzleWord,
  ignoreAttribute,
} from "@ascendnow/shared";
import { generatePuzzleWithTries, placeGeneratedPuzzle } from "./helpers";

export default function AscFindTheWordsPuzzleBoard({
  className,
  size = AscFindTheWordsPuzzleBoardSize.TEN_BY_TEN,
  words,
  generatedWords,
  solvedWords,
  onChange,
  onError,
  onGenerate,
  mode = "solve",
  disabled = false,
}: AscFindTheWordsPuzzleBoardProps) {
  const boardSizeStyle = {
    "--rows": size,
    "--columns": size,
  } as React.CSSProperties;

  const [cells, setCells] = useState<AscFindTheWordsPuzzleCellType[]>([]);
  const [placedWords, setPlacedWords] = useState<AscFindTheWordsPuzzleWord[]>(
    []
  );
  const [foundWords, setFoundWords] = useState<AscFindTheWordsPuzzleWord[]>([]);
  const [selectionBeginningId, setSelectionBeginningId] = useState<number>();
  const [selectionEndId, setSelectionEndId] = useState<number>();
  const [error, setError] = useState<string>();
  const generatePuzzle = useCallback(() => {
    if (!words || !words?.length) {
      return;
    }
    try {
      const { cells, placedWords, notPlacedWords } = generatePuzzleWithTries(
        size,
        words
      );
      setCells(cells);
      setPlacedWords(placedWords);
      onGenerate?.(placedWords, size);
      setError("");
      if (notPlacedWords?.length) {
        const error = `Failed to place words: ${notPlacedWords?.join(
          ", "
        )}, please try using fewer words or setting a larger puzzle size.`;
        setError(error);
        onError?.(error);
      }
    } catch (error) {
      onError?.(`${error}`);
      setError(`${error}`);
      onGenerate?.([], size);
      setPlacedWords([]);
    }
  }, [onError, onGenerate, size, words]);

  function beginOrEndSelection(id: number) {
    if (!selectionBeginningId && !selectionEndId) {
      setSelectionBeginningId(id);
    } else if (selectionBeginningId && !selectionEndId) {
      setSelectionEndId(id);
    } else if (selectionBeginningId && selectionEndId) {
      const word = placedWords?.find(
        (word) =>
          (word?.beginningCellNumber === selectionBeginningId &&
            word?.beginningCellNumber + (word?.length || 0) - 1 ===
              selectionEndId) ||
          (word?.beginningCellNumber === selectionEndId &&
            word?.beginningCellNumber + (word?.length || 0) - 1 ===
              selectionBeginningId) ||
          (word?.beginningCellNumber === selectionBeginningId &&
            word?.beginningCellNumber + ((word?.length || 0) - 1) * size ===
              selectionEndId) ||
          (word?.beginningCellNumber === selectionEndId &&
            word?.beginningCellNumber + ((word?.length || 0) - 1) * size ===
              selectionBeginningId)
      );
      if (word && !foundWords?.find((w) => word?.wordIndex === w?.wordIndex)) {
        setFoundWords(foundWords?.concat(word));
      }
      setSelectionBeginningId(undefined);
      setSelectionEndId(undefined);
    }
  }

  function endSelection(id: number) {
    if (selectionBeginningId) {
      setSelectionEndId(id);
    }
  }

  function isSelected(cell: AscFindTheWordsPuzzleCellType) {
    if (!selectionBeginningId) {
      return;
    }
    if (!selectionEndId) {
      return selectionBeginningId === cell?.id;
    }
    return (
      ((selectionBeginningId <= selectionEndId &&
        cell?.id >= selectionBeginningId &&
        cell.id <= selectionEndId) ||
        (selectionBeginningId > selectionEndId &&
          cell?.id <= selectionBeginningId &&
          cell.id >= selectionEndId)) &&
      ((cell?.row === cells[selectionBeginningId]?.row &&
        cell?.row === cells[selectionEndId]?.row) ||
        (cell?.column === cells[selectionBeginningId]?.column &&
          cell?.column === cells[selectionEndId]?.column))
    );
  }

  useEffect(() => {
    if (mode !== "create") {
      return;
    }
    generatePuzzle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size, words, mode]);

  useEffect(() => {
    if (mode !== "solve" || !generatedWords || !generatedWords?.length) {
      return;
    }
    const cells = placeGeneratedPuzzle(size, generatedWords);
    setCells(cells);
    setPlacedWords(generatedWords);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size, generatedWords, mode]);

  useEffect(() => {
    if (mode !== "completed" || !solvedWords || !solvedWords?.length) {
      return;
    }
    const cells = placeGeneratedPuzzle(size, solvedWords);
    setCells(cells);
    setPlacedWords(solvedWords);
    setFoundWords(solvedWords?.filter((w) => w?.found));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size, generatedWords, mode]);

  useEffect(() => {
    onChange?.(foundWords);
  }, [foundWords, onChange]);

  return (
    <div
      data-testid="asc-find-the-words-puzzle-board"
      className={classNames(styles.ground, className)}
      {...ignoreAttribute.contrast}
    >
      <div
        className={styles.board}
        style={boardSizeStyle}
        {...ignoreAttribute.contrast}
      >
        {cells?.slice(0, size * size)?.map((cell) => (
          <AscFindTheWordsPuzzleCell
            key={cell?.id}
            id={cell?.id}
            highlighted={foundWords?.some((word) =>
              cell?.wordIndices.includes(word?.wordIndex)
            )}
            readonly={mode === "create" || disabled}
            selected={isSelected(cell)}
            onSelect={beginOrEndSelection}
            onHover={endSelection}
            character={cell?.character}
            colors={cell?.wordIndices
              ?.map(
                (index) =>
                  foundWords?.find((word) => word?.wordIndex === index)
                    ?.color || ""
              )
              ?.filter((color) => !!color)}
          />
        ))}
      </div>
      {mode === "create" ? (
        <AscButton onClick={generatePuzzle}>Regenerate</AscButton>
      ) : null}
      {mode === "create" && error ? (
        <div className={styles.error} {...ignoreAttribute.contrast}>
          {error}
        </div>
      ) : null}
    </div>
  );
}
